<script lang="ts">
	import { Button as ButtonPrimitive } from 'bits-ui'
	import ChevronDown from 'lucide-svelte/icons/chevron-down'
	import ChevronUp from 'lucide-svelte/icons/chevron-up'
	import CircleHelp from 'lucide-svelte/icons/circle-help'
	import ToggleLeft from 'lucide-svelte/icons/toggle-left'
	import ToggleRight from 'lucide-svelte/icons/toggle-right'

	import InfoPopover from '$lib/components/info-popover.svelte'
	import Badge from '$lib/components/ui/badge/badge.svelte'
	import Skeleton from '$lib/components/ui/skeleton/skeleton.svelte'
	import * as Tooltip from '$lib/components/ui/tooltip'
	import * as m from '$lib/paraglide/messages'
	import { prettifyChange } from '$lib/utils/prettifyMetrics'
	import { cn } from '$lib/utils/shadcn'

	export let name = ''
	export let actionState = false
	export let actionText: string | null = null
	export let popoverContent: string | null = null
	export let popoverAriaLabel: string | null = null
	export let change: number | null | undefined = null
	export let popoverType: 'info' | 'warning' = 'info'
	export let value = ''
	export let isLoading = false
	export let isNew = false

	export let onActionClick: (() => void) | null = null
	export let onHintClick: (() => void) | null = null
</script>

<div
	class={cn('bg-background text-card-foreground relative rounded-lg border p-2 shadow-xs md:p-3', {
		'border-branding-blue': isNew,
	})}
>
	{#if isNew}
		<Badge class="absolute -top-2 -left-2 uppercase" size="3xs" variant="brand">
			{m.new_badge_text()}
		</Badge>
	{/if}

	<div class="flex min-h-[20px] items-center justify-between gap-0.5">
		<p class="text-muted-foreground xs:text-sm truncate text-xs font-medium">
			{name}
		</p>

		<div class="flex items-center justify-start gap-1.5">
			{#if onActionClick}
				<Tooltip.Root>
					<Tooltip.Trigger asChild disabled={isLoading} let:builder>
						<ButtonPrimitive.Root
							class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-sm transition-colors focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50"
							aria-label={actionText}
							builders={[builder]}
							disabled={isLoading}
							type="button"
							on:click={() => {
								onActionClick()
							}}
						>
							{#if actionState}
								<ToggleRight size="16" />
							{:else}
								<ToggleLeft size="16" />
							{/if}
						</ButtonPrimitive.Root>
					</Tooltip.Trigger>

					<Tooltip.Content>
						<!-- eslint-disable-next-line svelte/no-at-html-tags -->
						<p>{@html actionText}</p>
					</Tooltip.Content>
				</Tooltip.Root>
			{/if}

			{#if onHintClick}
				<button
					class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-full focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50"
					aria-label="Show explanation for {name}"
					disabled={isLoading}
					type="button"
					on:click={onHintClick}
				>
					<CircleHelp size="14" />
				</button>
			{/if}
		</div>
	</div>

	<div class="mt-2 flex items-center justify-start gap-1 md:mt-3">
		{#if isLoading}
			<Skeleton class="min-w-[50%]">
				<p class="xs:text-base text-sm font-medium">{value}</p>
			</Skeleton>
		{:else}
			<p class="xs:text-base text-sm font-medium">{value}</p>

			{#if change}
				<span
					class="flex items-center text-xs"
					class:text-green-500={change >= 0}
					class:text-red-400={change < 0}
				>
					{#if change >= 0}
						<ChevronUp size="13" />
					{:else}
						<ChevronDown size="13" />
					{/if}

					{prettifyChange(change)}
				</span>
			{/if}

			<InfoPopover ariaLabel={popoverAriaLabel} content={popoverContent} type={popoverType} />
		{/if}
	</div>
</div>
